import { type ClassValue, clsx } from "clsx";
import React from "react";
import {
  composeRenderProps,
  OverlayTriggerStateContext,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function composeTailwindRenderProps<T>(
  className: string | ((v: T) => string) | undefined,
  tw: string | string[],
): string | ((v: T) => string) {
  return composeRenderProps(className, (className) => twMerge(tw, className));
}

export function useMediaQuery(query: string) {
  const [value, setValue] = React.useState(false);

  React.useEffect(() => {
    const onChange = (event: MediaQueryListEvent) => {
      setValue(event.matches);
    };

    const result = matchMedia(query);
    result.addEventListener("change", onChange);
    setValue(result.matches);

    return () => result.removeEventListener("change", onChange);
  }, [query]);

  return value;
}

export const focusRing = tv({
  base: "outline-0 outline-ring/70 outline-offset-2 focus-visible:outline-2 invalid:outline-destructive",
});

export const inputFocusRing = tv({
  base: [
    "focus:border-ring focus:outline-hidden focus:ring-[3px] focus:ring-ring/20 transition-shadow",
    "focus:invalid:border-destructive focus:invalid:ring-destructive/20",
    "focus:group-invalid:border-destructive focus:group-invalid:ring-destructive/20",
  ],
});

export const displayLevels = {
  1: "font-semibold text-lg",
  2: "font-semibold text-base",
  3: "font-medium text-sm",
};

export type DisplayLevel = keyof typeof displayLevels;

/**
 * Looks like some silly hack to check if a menu is open.
 */
export function isMenuOpen() {
  const menu = document.querySelector('[role="listbox"], [role="menu"]');
  return menu !== null;
}

/**
 * Get all keyboard focusable elements within an element.
 */
export function getKeyboardFocusableElements(
  element: HTMLElement,
): HTMLElement[] {
  return [
    ...element.querySelectorAll(
      'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"]), [data-focusable]',
    ),
  ].filter(
    (el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"),
  ) as HTMLElement[];
}

export function useDialogState() {
  const ctx = React.useContext(OverlayTriggerStateContext);
  if (!ctx) {
    throw new Error("useDialogState must be used within a DialogProvider");
  }
  return ctx;
}

export const twColorMap = {
  red: "bg-red-100 dark:bg-red-400/50",
  orange: "bg-orange-100 dark:bg-orange-400/50",
  amber: "bg-amber-100 dark:bg-amber-400/50",
  yellow: "bg-yellow-100 dark:bg-yellow-400/50",
  lime: "bg-lime-100 dark:bg-lime-400/50",
  green: "bg-green-100 dark:bg-green-400/50",
  emerald: "bg-emerald-100 dark:bg-emerald-400/50",
  teal: "bg-teal-100 dark:bg-teal-400/50",
  cyan: "bg-cyan-100 dark:bg-cyan-400/50",
  sky: "bg-sky-100 dark:bg-sky-400/50",
  blue: "bg-blue-100 dark:bg-blue-400/50",
  indigo: "bg-indigo-100 dark:bg-indigo-400/50",
  violet: "bg-violet-100 dark:bg-violet-400/50",
  purple: "bg-purple-100 dark:bg-purple-400/50",
  fuchsia: "bg-fuchsia-100 dark:bg-fuchsia-400/50",
  pink: "bg-pink-100 dark:bg-pink-400/50",
  rose: "bg-rose-100 dark:bg-rose-400/50",
  muted: "bg-muted",
};

export function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

type Color = keyof typeof twColorMap;

export function getRandomColor(seed: number | undefined, exclude?: Color[]) {
  if (!seed) {
    return undefined;
  }
  const colors = Object.keys(twColorMap).filter(
    (k) => !exclude?.includes(k as Color),
  ) as Color[];
  const randomIndex = Math.floor(seededRandom(seed) * colors.length);
  return colors[randomIndex];
}
